import type { Directive } from 'vue'

/**
 * Клавиатурное поведение диалогов (модалки, drawer, самописные окна):
 * фокус уходит внутрь при открытии, Tab не выходит за пределы окна,
 * Escape закрывает только верхнее окно, а после закрытия фокус возвращается
 * на элемент, который открыл диалог.
 */
export interface DialogFocusOptions {
  /** Вызывается по Escape, если окно верхнее. Без обработчика Escape игнорируется. */
  onEscape?: (() => void) | undefined
}

interface DialogState {
  previous: HTMLElement | null
  options: DialogFocusOptions | undefined
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const states = new WeakMap<HTMLElement, DialogState>()
/** Открытые диалоги, последний - верхний. */
const stack: HTMLElement[] = []
let listening = false

function isVisible(el: HTMLElement): boolean {
  if (el.closest('[hidden], [aria-hidden="true"]') !== null) return false
  // checkVisibility учитывает display:none и visibility:hidden у предков; в окружениях без него
  // (jsdom) считаем элемент видимым
  return typeof el.checkVisibility === 'function' ? el.checkVisibility({ visibilityProperty: true }) : true
}

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(isVisible)
}

function onDocumentKeydown(event: KeyboardEvent): void {
  const top = stack[stack.length - 1]
  if (!top) return

  if (event.key === 'Escape') {
    const handler = states.get(top)?.options?.onEscape
    if (handler) {
      event.stopPropagation()
      handler()
    }
    return
  }

  if (event.key !== 'Tab') return

  const items = focusableWithin(top)
  if (items.length === 0) {
    event.preventDefault()
    top.focus()
    return
  }

  const first = items[0] as HTMLElement
  const last = items[items.length - 1] as HTMLElement
  const active = document.activeElement
  const inside = active instanceof HTMLElement && top.contains(active)

  if (event.shiftKey && (!inside || active === first || active === top)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && (!inside || active === last)) {
    event.preventDefault()
    first.focus()
  }
}

function startListening(): void {
  if (listening) return
  document.addEventListener('keydown', onDocumentKeydown, true)
  listening = true
}

function stopListening(): void {
  if (!listening || stack.length > 0) return
  document.removeEventListener('keydown', onDocumentKeydown, true)
  listening = false
}

export const vDialogFocus: Directive<HTMLElement, DialogFocusOptions | undefined> = {
  mounted(el, binding) {
    const active = document.activeElement
    states.set(el, {
      previous: active instanceof HTMLElement && active !== document.body ? active : null,
      options: binding.value,
    })
    stack.push(el)
    startListening()

    if (!el.hasAttribute('tabindex')) el.tabIndex = -1
    // Автофокус: явный [autofocus], затем первое поле формы. Если полей нет, фокус на самом окне:
    // первая кнопка может оказаться деструктивной («Заблокировать», «Удалить»), Enter не должен её нажать
    const preferred =
      el.querySelector<HTMLElement>('[autofocus]') ??
      focusableWithin(el).find((item) => /^(INPUT|SELECT|TEXTAREA)$/.test(item.tagName)) ??
      el
    preferred.focus()
  },
  updated(el, binding) {
    const state = states.get(el)
    if (state) state.options = binding.value
  },
  unmounted(el) {
    const state = states.get(el)
    const index = stack.indexOf(el)
    if (index !== -1) stack.splice(index, 1)
    states.delete(el)
    stopListening()

    const previous = state?.previous
    if (previous && previous.isConnected) previous.focus()
  },
}
